import { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Form,
  Formik,
  getIn,
  type FormikErrors,
  type FormikTouched,
} from "formik";
import { useTranslation } from "react-i18next";

import { toFormikValidate } from "@thalamos/common";
import type {
  PersonDetailsUnbranded,
  PersonNameUnbranded,
  PersonNameUse,
  UpdatePersonRequest,
} from "@thalamos/haven-common";
import {
  PersonNameUseSchema,
  UpdatePersonRequestSchema,
} from "@thalamos/haven-common";

/**
 * Whether the change landed. Every failure is one outcome, because nothing the
 * person in front of the form could do distinguishes them.
 */
export type PersonSaveOutcome = "saved" | "failed";

/**
 * What the form edits: what the young person is called and when they were born.
 *
 * Not their identifiers. What another system calls them is stated when the
 * record is registered and will come from a link to that system rather than
 * from typing — see
 * `docs/decisions/2026-09-10-an-identifier-is-set-when-a-record-is-registered.md`.
 */
export type PersonValues = PersonDetailsUnbranded;

/** A record with nothing filled in yet, for the registration page. */
export const emptyPersonValues = (): PersonValues => ({
  dateOfBirth: null,
  names: [emptyName()],
});

const emptyName = (): PersonNameUnbranded => ({
  use: "official",
  family: null,
  given: [],
  periodStart: null,
  periodEnd: null,
});

/**
 * The key the validation adaptor files an issue under. It joins the issue's path
 * with dots after dropping falsy segments — which drops a zero index, so an
 * issue at `names.0.family` is filed as `names.family` while one at
 * `names.1.family` keeps its index. Mirrored here rather than corrected, because
 * the adaptor is shared and its behaviour is what the keys are.
 */
const adaptorKey = (path: string): string =>
  path
    .split(".")
    .filter((segment) => segment !== "0")
    .join(".");

/**
 * The adaptor reports an issue as one flat key, while Formik's own bookkeeping
 * of what has been touched is nested. Both are read here so a field is marked
 * once it has been left, with the message the schema gave it.
 */
const fieldError = (
  errors: FormikErrors<PersonValues>,
  touched: FormikTouched<PersonValues>,
  path: string,
): string | undefined => {
  const message = (errors as Record<string, unknown>)[adaptorKey(path)];
  const wasTouched: unknown = getIn(touched, path);

  return typeof message === "string" && wasTouched === true
    ? message
    : undefined;
};

/** The field's helper text: its error where it has one, else its own hint. */
const helperText = (
  t: (key: string) => string,
  error: string | undefined,
  hint?: string,
): string | undefined => (error === undefined ? hint : t(error));

/**
 * Given names are typed as one field and stored as several. The raw text is kept
 * only while it still means what the array says, so a record re-seeded from
 * outside the field — after a save, or on a different record — shows the array
 * rather than a stale draft, and nobody's trailing space is eaten mid-word.
 */
const splitGiven = (text: string): string[] =>
  text.split(/\s+/).filter((part) => part.length > 0);

const GivenNamesField = ({
  id,
  given,
  error,
  onChange,
  onBlur,
}: {
  id: string;
  given: string[];
  error: string | undefined;
  onChange: (given: string[]) => void;
  onBlur: () => void;
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string | undefined>(undefined);
  const shown =
    draft !== undefined && splitGiven(draft).join(" ") === given.join(" ")
      ? draft
      : given.join(" ");

  return (
    <TextField
      id={id}
      label={t("personPage.names.given")}
      value={shown}
      onChange={(event) => {
        setDraft(event.target.value);
        onChange(splitGiven(event.target.value));
      }}
      onBlur={onBlur}
      error={error !== undefined}
      helperText={helperText(t, error, t("personPage.names.givenHelp"))}
      fullWidth
    />
  );
};

/**
 * The form for correcting a young person's record; the page around it supplies
 * the heading and the save.
 *
 * Validation is `UpdatePersonRequestSchema`, the same schema the server refuses
 * the write with, so the form cannot call acceptable a value the endpoint will
 * reject.
 *
 * `onSave` is injected rather than the component reaching for the API client, so
 * a story or a test can show any state it has without a session or a router.
 */
export const PersonForm = ({
  initialValues,
  submitLabel,
  onSave,
}: {
  initialValues: PersonValues;
  submitLabel: string;
  onSave: (request: UpdatePersonRequest) => Promise<PersonSaveOutcome>;
}) => {
  const { t } = useTranslation();

  return (
    <Formik<PersonValues>
      initialValues={initialValues}
      // Re-seeded when the record it is editing changes, so a draft typed
      // against one record is not carried into the next.
      enableReinitialize
      validate={toFormikValidate(UpdatePersonRequestSchema)}
      onSubmit={async (values, helpers) => {
        const parsed = UpdatePersonRequestSchema.safeParse(values);

        // Unreachable: Formik calls onSubmit only once `validate` has passed the
        // same values against the same schema. Kept to fail loudly if not.
        /* v8 ignore if */
        if (!parsed.success) {
          throw new Error("Expected values Formik validated to parse", {
            cause: parsed.error,
          });
        }

        // A rejection is caught here rather than left to Formik, which would
        // report it to the console and leave a form that did nothing and said
        // nothing.
        const outcome = await onSave(parsed.data).catch(
          (): PersonSaveOutcome => "failed",
        );

        // `status` rather than a field error: a failure is about the request,
        // and Formik clears field errors on the next keystroke.
        helpers.setStatus(outcome === "saved" ? undefined : outcome);

        if (outcome === "saved") {
          helpers.resetForm({ values });
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        status,
        dirty,
        isSubmitting,
        handleChange,
        handleBlur,
        setFieldValue,
        setFieldTouched,
      }) => {
        return (
          <Form noValidate>
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack spacing={3}>
                  <TextField
                    id="person-date-of-birth"
                    name="dateOfBirth"
                    type="date"
                    label={t("personPage.dateOfBirth.label")}
                    value={values.dateOfBirth ?? ""}
                    onChange={(event) => {
                      void setFieldValue(
                        "dateOfBirth",
                        event.target.value === "" ? null : event.target.value,
                      );
                    }}
                    onBlur={handleBlur}
                    error={
                      fieldError(errors, touched, "dateOfBirth") !== undefined
                    }
                    helperText={helperText(
                      t,
                      fieldError(errors, touched, "dateOfBirth"),
                      t("personPage.dateOfBirth.help"),
                    )}
                    slotProps={{ inputLabel: { shrink: true } }}
                    sx={{ maxWidth: 280 }}
                  />
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack spacing={3}>
                  <Stack spacing={0.5}>
                    <Typography variant="h6" component="h2">
                      {t("personPage.names.title")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("personPage.names.description")}
                    </Typography>
                  </Stack>

                  {typeof errors.names === "string" ? (
                    <Alert severity="error">{t(errors.names)}</Alert>
                  ) : null}

                  {values.names.map((name, index) => {
                    const at = (field: string) =>
                      `names.${String(index)}.${field}`;

                    return (
                      <Stack
                        // Names have no identity of their own, so position is the
                        // only key there is; a removal re-keys the rows after it.
                        key={index}
                        spacing={2}
                        sx={{
                          borderTop: index === 0 ? 0 : 1,
                          borderColor: "divider",
                          pt: index === 0 ? 0 : 2,
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            id={`person-name-${String(index)}-use`}
                            name={at("use")}
                            select
                            label={t("personPage.names.use")}
                            value={name.use}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            sx={{ minWidth: 180 }}
                          >
                            {PersonNameUseSchema.options.map(
                              (use: PersonNameUse) => (
                                <MenuItem key={use} value={use}>
                                  {t(`personPage.names.useLabel.${use}`)}
                                </MenuItem>
                              ),
                            )}
                          </TextField>
                          <GivenNamesField
                            id={`person-name-${String(index)}-given`}
                            given={name.given}
                            error={
                              fieldError(errors, touched, at("given")) ??
                              // A part that is empty or too long is reported on
                              // the part; shown against the one field they share.
                              name.given
                                .map((_part, partIndex) =>
                                  fieldError(
                                    errors,
                                    touched,
                                    `${at("given")}.${String(partIndex)}`,
                                  ),
                                )
                                .find((error) => error !== undefined)
                            }
                            onChange={(given) => {
                              void setFieldValue(at("given"), given);
                            }}
                            onBlur={() => {
                              void setFieldTouched(at("given"), true);
                            }}
                          />
                          <TextField
                            id={`person-name-${String(index)}-family`}
                            name={at("family")}
                            label={t("personPage.names.family")}
                            value={name.family ?? ""}
                            onChange={(event) => {
                              void setFieldValue(
                                at("family"),
                                event.target.value === ""
                                  ? null
                                  : event.target.value,
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              fieldError(errors, touched, at("family")) !==
                              undefined
                            }
                            helperText={helperText(
                              t,
                              fieldError(errors, touched, at("family")),
                            )}
                            fullWidth
                          />
                        </Stack>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                          alignItems="flex-start"
                        >
                          <TextField
                            id={`person-name-${String(index)}-period-start`}
                            name={at("periodStart")}
                            type="date"
                            label={t("personPage.names.periodStart")}
                            value={name.periodStart ?? ""}
                            onChange={(event) => {
                              void setFieldValue(
                                at("periodStart"),
                                event.target.value === ""
                                  ? null
                                  : event.target.value,
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              fieldError(errors, touched, at("periodStart")) !==
                              undefined
                            }
                            helperText={helperText(
                              t,
                              fieldError(errors, touched, at("periodStart")),
                            )}
                            slotProps={{ inputLabel: { shrink: true } }}
                          />
                          <TextField
                            id={`person-name-${String(index)}-period-end`}
                            name={at("periodEnd")}
                            type="date"
                            label={t("personPage.names.periodEnd")}
                            value={name.periodEnd ?? ""}
                            onChange={(event) => {
                              void setFieldValue(
                                at("periodEnd"),
                                event.target.value === ""
                                  ? null
                                  : event.target.value,
                              );
                            }}
                            onBlur={handleBlur}
                            error={
                              fieldError(errors, touched, at("periodEnd")) !==
                              undefined
                            }
                            helperText={helperText(
                              t,
                              fieldError(errors, touched, at("periodEnd")),
                            )}
                            slotProps={{ inputLabel: { shrink: true } }}
                          />
                          {values.names.length > 1 ? (
                            <IconButton
                              aria-label={t("personPage.names.remove")}
                              onClick={() => {
                                void setFieldValue(
                                  "names",
                                  values.names.filter(
                                    (_name, other) => other !== index,
                                  ),
                                );
                              }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          ) : null}
                        </Stack>
                      </Stack>
                    );
                  })}

                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => {
                      void setFieldValue("names", [
                        ...values.names,
                        emptyName(),
                      ]);
                    }}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    {t("personPage.names.add")}
                  </Button>
                </Stack>
              </Paper>

              {status === "failed" ? (
                <Alert severity="error">{t("personPage.saveFailed")}</Alert>
              ) : null}

              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  type="submit"
                  variant="contained"
                  // Nothing to save is not a thing to offer: a button that is
                  // always live invites a request that changes nothing.
                  disabled={!dirty || isSubmitting}
                >
                  {submitLabel}
                </Button>
                {!dirty && status === undefined ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("personPage.upToDate")}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
};
